import {
  palette,
  Table,
  TableRow,
  TableCell,
  ProtectedField,
  DialogV2,
  Loading,
  Tooltip,
  Icon,
} from '@yuno/design-system-web'
import { TTableResponsive } from '../../../../typings/pages'
import { CardTitle, Content } from '../../style'
import { useEffect, useState } from 'react'
import {
  CardKey,
  CardMobile,
  IconContent,
  IconWrapper,
  InputStyled,
  LoadingWrapper,
  RowCardMobile,
  SpanDialogPassoword,
  SpanStyled,
  SpanValue,
  TableCellStyled,
  TitleDialog,
} from './style'
import { usePostValidatePassword } from '@yuno/dashboard-api-mfe'
import { useForm } from 'react-hook-form'
import { keyDownConstants } from '../../../../utils/constants'
import { useTranslation } from 'react-i18next'

const TableResponsive = ({
  columnsTable,
  isLoading,
  rows,
  onCopied,
  title,
  subtitle,
  withCopy,
  withShow,
  setMessageSnack,
  allowed = true,
  needPassword = false,
}: TTableResponsive) => {
  const [showValueDesktop, setShowValueDesktop] = useState<boolean[]>(
    rows.map(() => true),
  )
  const [showValueMobile, setShowValueMobile] = useState<boolean[]>(
    rows.map(() => true),
  )
  const [keyShow, setKeyShow] = useState(null)
  const [isMobileShow, setIsMobileShow] = useState(null)
  const [openDialog, setOpenDialog] = useState(false)
  const [passwordInput, setPasswordInput] = useState('')
  const [isValidPass, setIsValidPass] = useState(needPassword)
  const validatePassword = usePostValidatePassword(passwordInput)
  const { t } = useTranslation()

  const {
    register,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      oneTimeCode: '',
    },
  })

  const handleClick = (value: string) => {
    if ((withShow && isValidPass) || (!withShow && withCopy)) {
      navigator.clipboard.writeText(value)
      onCopied()
      setMessageSnack(t('copyClipboard'))
    }
  }

  const handleShowValid = (key: number, isMobile: boolean) => {
    const newArray = [...(isMobile ? showValueMobile : showValueDesktop)]
    newArray[key] = isMobile ? !showValueMobile[key] : !showValueDesktop[key]
    if (isMobile) {
      setShowValueMobile(newArray)
    } else {
      setShowValueDesktop(newArray)
    }
    setOpenDialog(false)
  }

  const handleShow = (key: number, isMobile: boolean) => {
    if (allowed) {
      if (isValidPass) {
        handleShowValid(key, isMobile)
      } else {
        setKeyShow(key)
        setIsMobileShow(isMobile)
        clearErrors()
        setPasswordInput('')
        setOpenDialog(true)
      }
    }
  }

  const handleInputPassword = (e) => {
    setPasswordInput(e.target.value)
    clearErrors()
  }

  const handleSubmitDialog = () => {
    validatePassword.mutate()
  }

  const handleKeyDown = (e) => {
    if (e.code === keyDownConstants.ENTER) {
      e.preventDefault()
      e.stopPropagation()
      handleSubmitDialog()
    }
  }

  useEffect(() => {
    if (validatePassword.isError) {
      const error = validatePassword.error.response.data?.data
      if (error.code === '401') {
        setError(
          'password',
          { message: t('wrongPassword') },
          { shouldFocus: true },
        )
      }
    }
  }, [validatePassword.isError])

  useEffect(() => {
    if (
      validatePassword.status === 'success' &&
      validatePassword.data === null
    ) {
      setError(
        'password',
        { message: t('connectionFailed') },
        { shouldFocus: true },
      )
    }
  }, [validatePassword.status])

  useEffect(() => {
    if (passwordInput !== '' && validatePassword.isSuccess) {
      setIsValidPass(true)
      setOpenDialog(false)
      handleShowValid(keyShow, isMobileShow)
      onCopied()
      setMessageSnack(t('keyUnloked'))
    }
  }, [validatePassword.isSuccess])

  useEffect(() => {
    setIsValidPass(needPassword)
  }, [needPassword])

  return (
    <CardKey>
      <CardTitle>{title}</CardTitle>
      {subtitle ? <Content>{subtitle}</Content> : <></>}
      <Table
        ref={null}
        columns={columnsTable}
        loading={isLoading}
        showEmptyTable={false}
      >
        {rows.map((row, key) => {
          const nameIcon = showValueDesktop[key] ? 'Eye' : 'EyeSlash'
          const textShow = showValueDesktop[key]
            ? t('showToken')
            : t('hideToken')
          return (
            <TableRow hover tabIndex={-1} key={key}>
              <TableCell width={'20%'}>{row.label}</TableCell>
              <TableCellStyled
                width={'45%'}
                height={!showValueDesktop[key] ? '100px' : '50px'}
              >
                {!withShow || !showValueDesktop[key] ? (
                  <SpanValue>{row.value}</SpanValue>
                ) : (
                  <ProtectedField
                    text={row.value}
                    protect={withShow && showValueDesktop[key]}
                  />
                )}
              </TableCellStyled>
              <TableCell width={'50%'}>
                <IconWrapper>
                  {withCopy && (
                    <IconContent
                      disabled={withShow && !isValidPass}
                      onClick={handleClick.bind(null, row.value)}
                    >
                      <Icon
                        color={
                          withShow && !isValidPass
                            ? palette.grey[3]
                            : palette.grey[5]
                        }
                        name={'Copy'}
                        size="16"
                        className="IconText"
                      />
                      <SpanStyled
                        disabled={withShow && !isValidPass}
                        className="IconText"
                      >
                        {t('copy')}
                      </SpanStyled>
                    </IconContent>
                  )}
                  {withShow && (
                    <Tooltip
                      disableHoverListener={allowed}
                      title={t('noAccess')}
                      color="default"
                      arrow
                    >
                      <IconContent
                        onClick={handleShow.bind(null, key, false)}
                        disabled={!allowed}
                      >
                        <Icon
                          color={!allowed ? palette.grey[3] : palette.grey[5]}
                          name={nameIcon}
                          size="16"
                        />
                        <SpanStyled disabled={!allowed} className="IconText">
                          {textShow}
                        </SpanStyled>
                      </IconContent>
                    </Tooltip>
                  )}
                </IconWrapper>
              </TableCell>
            </TableRow>
          )
        })}
      </Table>
      <div>
        {rows.map((row, key) => {
          const nameIcon = showValueMobile[key] ? 'Eye' : 'EyeSlash'
          const textShow = showValueMobile[key]
            ? t('showToken')
            : t('hideToken')
          return (
            <CardMobile key={`key-first-row-${key}`}>
              <RowCardMobile>{row.label}</RowCardMobile>
              <RowCardMobile>
                {!withShow || !showValueMobile[key] ? (
                  <SpanValue>{row.value}</SpanValue>
                ) : (
                  <ProtectedField
                    text={row.value}
                    protect={withShow && showValueMobile[key]}
                  />
                )}
              </RowCardMobile>
              <div>
                <IconWrapper>
                  {withCopy && (
                    <IconContent
                      disabled={withShow && !isValidPass}
                      onClick={handleClick.bind(null, row.value)}
                    >
                      <Icon
                        color={
                          !withShow && !isValidPass
                            ? palette.grey[1]
                            : palette.grey[5]
                        }
                        name={'Copy'}
                        size="16"
                        className="IconText"
                      />
                      <SpanStyled
                        disabled={withShow && !isValidPass}
                        className="IconText"
                      >
                        {t('copy')}
                      </SpanStyled>
                    </IconContent>
                  )}
                  {withShow && (
                    <Tooltip
                      disableHoverListener={allowed}
                      title={t('noAccess')}
                      color="default"
                      arrow
                    >
                      <IconContent
                        onClick={handleShow.bind(null, key, true)}
                        disabled={!allowed}
                      >
                        <Icon
                          color={!allowed ? palette.grey[3] : palette.grey[5]}
                          name={nameIcon}
                          size="16"
                        />
                        <SpanStyled disabled={!allowed} className="IconText">
                          {textShow}
                        </SpanStyled>
                      </IconContent>
                    </Tooltip>
                  )}
                </IconWrapper>
              </div>
            </CardMobile>
          )
        })}
        <>
          <DialogV2
            open={openDialog}
            widthContainer={520}
            buttons={[
              {
                disabled: false,
                event: () => setOpenDialog(false),
                label: t('cancel'),
                size: 'small',
                variant: 'tertiary',
              },
              {
                disabled: passwordInput === '' || validatePassword.isLoading,
                event: () => handleSubmitDialog(),
                label: !validatePassword.isLoading ? (
                  t('submit')
                ) : (
                  <LoadingWrapper>
                    <Loading />
                  </LoadingWrapper>
                ),
                size: 'small',
                variant: 'primary',
              },
            ]}
            closeIcon={() => setOpenDialog(false)}
            title={<TitleDialog>{t('unlockKeys')}</TitleDialog>}
          >
            <SpanDialogPassoword>{t('needPassword')}</SpanDialogPassoword>
            <form id="edit-form" onSubmit={handleSubmitDialog}>
              <InputStyled
                {...register('password')}
                value={passwordInput}
                onChange={handleInputPassword}
                placeholder={t('enterPassword')}
                fullWidth
                error={!!errors.password}
                onKeyDown={handleKeyDown}
                helperText={errors.password ? t('wrongPassword') : ''}
              />
            </form>
          </DialogV2>
        </>
      </div>
    </CardKey>
  )
}

export default TableResponsive
